import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Breadcrumbs, Divider, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, Modal, OutlinedInput, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import storage from '../../../../config/storage';


import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ARROW_LEFT from '../../../../assets/images/back.svg';
import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg';
import RESUME_EXAM_ICON from '../../../../assets/images/ResumeExam.svg';

import { useContext, useEffect, useState } from 'react';
import { ProjectContext } from '../../../../providers/projectProvider';
import RadiusButton from '../../../../components/RadiusButton';
import { getServerSummaryList, resumeExam } from '../../../../services/candidateService';
import { useToaster } from '../../../../providers/ToasterProvider';

const columns = [
    { id: 'rollNo', label: 'Roll No', minWidth: 140 },
    { id: 'candidateName', label: 'Candidate Name', minWidth: 140 },
    { id: 'examStarted', label: 'Exam Started', minWidth: 140 },
    { id: 'biometricCaptured', label: 'Biometric', minWidth: 140 },
    { id: 'loggedIn', label: 'Logged In', minWidth: 140 },
    { id: 'usedTime', label: 'Used Duration', minWidth: 140 },
    { id: 'remainingTime', label: 'Remaining Duration', minWidth: 140 },
    { id: 'examEnded', label: 'Exam Over', minWidth: 140 },
    { id: 'rPacksUploaded', label: 'Rpacks Uploaded', minWidth: 140 },
    { id: 'logUploaded', label: 'Audit Logs Uploaded', minWidth: 140 },
    { id: 'action', label: 'Action', minWidth: 140 },
];

const resumePasswordSchema = z.object({
    password: z.string().min(1, { message: 'Password is required.' }),
});
const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT);

export const ServerSummary = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const examMode = storage.getExamMode();
    const isVenueMode = examMode === 'VENUE_BASED';
    const projectName = params?.projectName;
    const [searchParams] = useSearchParams();
    const status = searchParams.get('filter'); // LOGGED_IN, ABSENT, EXAM_STARTED...


    const { projectDetails } = useContext(ProjectContext);
    const { addNotification } = useToaster();
    const [projectMasterId, setProjectMasterId] = useState(null);
    const [serverId, setServerId] = useState(null);
    const [serverName, setServerName] = useState(null);
    const [examHallId, setExamHallId] = useState(null);
    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [candidates, setCandidates] = useState([]);
    const [courseName, setCourseName] = useState(null);
    const [refreshList, setRefreshList] = useState(false);

    const [resumeModal, setResumeModal] = useState({ isVisible: false, candidateId: null, rollNo: '' });
    const [isResuming, setIsResuming] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const {
        register: registerResume,
        handleSubmit: handleResumeSubmit,
        formState: { errors: resumeErrors },
        trigger: triggerResume,
        reset: resetResumeForm,
    } = useForm({
        resolver: zodResolver(resumePasswordSchema),
    });


    const handlePageChange = (e, value) => {
        setCurrentPage(value)
    };

    const openResumeModal = (candidate) => {
        setResumeModal({ isVisible: true, candidateId: candidate.candidateId, rollNo: candidate.rollNo });
    };

    const closeResumeModal = () => {
        if (isResuming) return;
        setResumeModal({ isVisible: false, candidateId: null, rollNo: '' });
        setPasswordVisible(false);
        resetResumeForm();
    };

    const handleResumeExam = async (data) => {
        if (!resumeModal.candidateId || !data.password) return;
        setIsResuming(true);
        try {
            const res = await resumeExam(projectMasterId, resumeModal.candidateId, data.password);
            if (res.code === 'OK') {
                addNotification('Exam resumed successfully.', 'success');
                setPasswordVisible(false);
                resetResumeForm();
                setResumeModal({ isVisible: false, candidateId: null, rollNo: '' });
                setRefreshList((value) => !value);
            }
        } catch (error) {
            const message = error?.response?.data?.message ?? 'Failed to resume exam.';
            addNotification(message, 'danger');
            console.log('There is an error while resuming exam', error);
        } finally {
            setIsResuming(false);
        }
    };

    useEffect(() => {
        const projectMasterId = location?.state?.projectMasterId;
        const serverId = location?.state?.serverId;
        const serverName = location?.state?.serverName;
        const examHallId = location?.state?.examHallId;
        const examHallName = location?.state?.examHallName;
        setProjectMasterId(projectMasterId);
        setServerId(serverId);
        setExamHallId(examHallId);
        setServerName(serverName);
    }, [location]);

    useEffect(() => {
        const fetchServerSummary = async () => {
            try {
                const res = await getServerSummaryList(projectMasterId, serverId, examHallId, currentPage, limit, status);
                if (res.code === 'OK') {
                    const totalPages = Math.ceil(res.data.count / limit);
                    setMaxPages(totalPages);
                    setCandidates(res.data.rows);
                    setCourseName(res.data.courseName);
                }
            } catch (error) {
                console.log('There is an error while fetching exam summary', error);
            }
        }
        if (projectMasterId && serverId && examHallId) {
            fetchServerSummary();
        } else {
            // todo
        }
    }, [projectMasterId, serverId, examHallId, currentPage, status, refreshList])

    const onGoBack = () => {
        if (projectName) {
            navigate(`/${projectName}/exam-summary`, {
                state: {
                    projectMasterId: projectMasterId,
                }
            });
        }
    }

    let filteredColumns = [...columns];

    if (!isVenueMode) {
        filteredColumns = filteredColumns.filter(col =>
            !['biometricCaptured', 'rPacksUploaded', 'logUploaded'].includes(col.id)
        );
    }

    if (status) {
        filteredColumns = filteredColumns.filter(col =>
            !['examStarted', 'loggedIn', 'examEnded'].includes(col.id)
        );
    }

    if (status !== 'EXAM_SUBMITTED') {
        filteredColumns = filteredColumns.filter(col => col.id !== 'action');
    }

    const renderTableCell = (columnId, candidateIndex) => {
        const candidate = candidates[candidateIndex];
        switch (columnId) {
            case 'rollNo':
                return candidate['rollNo']
            case 'candidateName':
                return candidate['candidateName']
            case 'examStarted':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['examStarted'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['examStarted'] ? 'Yes' : 'No'}
                </Box>
            case 'biometricCaptured':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['biometricCaptured'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['biometricCaptured'] ? 'Yes' : 'No'}
                </Box>
            case 'loggedIn':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['loggedIn'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['loggedIn'] ? 'Yes' : 'No'}
                </Box>
            case 'examEnded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['examEnded'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['examEnded'] ? 'Yes' : 'No'}
                </Box>
            case 'usedTime':
                return (
                    <Typography>
                        {candidate.usedTime || '00:00:00'}
                    </Typography>
                );

            case 'remainingTime':
                return (
                    <Typography>
                        {candidate.remainingTime || '00:00:00'}
                    </Typography>
                );


            case 'rPacksUploaded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['rPacksUploaded'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['rPacksUploaded'] ? 'Yes' : 'No'}
                </Box>
            case 'logUploaded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['logUploaded'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['logUploaded'] ? 'Yes' : 'No'}
                </Box>
            case 'action':
                const isRowResuming = isResuming && resumeModal.candidateId === candidate.candidateId;
                return <Tooltip title='Resume Exam'>
                    <span>
                        <IconButton
                            size='small'
                            disabled={isRowResuming}
                            onClick={() => openResumeModal(candidate)}
                            sx={{
                                bgcolor: 'primary.50',
                                '&:hover': { bgcolor: 'primary.100' },
                                '&.Mui-disabled': { bgcolor: 'grey.100', opacity: 0.5 },
                            }}
                        >
                            <img src={RESUME_EXAM_ICON} alt='' style={{ height: 18, width: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            default:
                return null;
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Server Summary</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                    mb: 3
                }}>
                    <Box>
                        <Typography variant='h6'>
                            Server Id:
                            <Typography component='span' variant='h6' sx={{ ml: 1, color: 'primary.main' }}>{serverName}</Typography>
                        </Typography>
                        <Typography variant='h6' sx={{ mb: 2 }}>
                            Course Name:
                            <Typography component='span' variant='h6' sx={{ ml: 1, color: 'primary.main' }}>
                                {courseName || '-'}
                            </Typography>
                        </Typography>

                    </Box>
                    <RadiusButton sx={{
                        borderRadius: 40,
                        textTransform: 'capitalize',
                        ml: 'auto'
                    }} color="grey"
                        variant='contained'
                        size='large'
                        onClick={onGoBack}
                        startIcon={<img src={ARROW_LEFT} alt='' />}
                    >Go back</RadiusButton>
                </Box>


                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 305px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {filteredColumns.map((column) => (

                                        <TableCell
                                            sx={{
                                                color: 'primary.main'
                                            }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!candidates?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={11} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : candidates.map((candidate, candidateIndex) => {
                                        return <TableRow hover={false} role="checkbox" key={candidateIndex}>
                                            {filteredColumns.map((column) => {
                                                return <TableCell key={column.id} align='center' sx={{
                                                    px: 1.5,
                                                    py: 0.75
                                                }}>
                                                    {renderTableCell(column.id, candidateIndex)}
                                                </TableCell>
                                            })}
                                        </TableRow>
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Pagination sx={{ mt: 'auto' }} count={maxPages} page={currentPage} onChange={handlePageChange} shape="rounded" />

                <Modal open={resumeModal.isVisible} className='backdrop' hideBackdrop={true} onClose={closeResumeModal}>
                    <Box className='modal-container' component='form' autoComplete='off' sx={{
                        maxWidth: 420,
                        p: 4,
                    }} onSubmit={handleResumeSubmit(handleResumeExam)}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 2.5
                        }}>
                            <Typography variant='h6' sx={{ fontWeight: 600 }}>
                                Resume Exam
                            </Typography>
                            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                                Roll No: <Box component='span' sx={{ color: 'primary.main', fontWeight: 600 }}>{resumeModal.rollNo}</Box>
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        <FormControl variant='outlined' sx={{ width: '100%', mb: 1 }}>
                            <InputLabel htmlFor="resumePasswordLabel">Password</InputLabel>
                            <OutlinedInput
                                id='resumePasswordLabel'
                                type={passwordVisible ? 'text' : 'password'}
                                sx={{ bgcolor: 'background.base' }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setPasswordVisible(!passwordVisible)}
                                            edge="end"
                                        >
                                            <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Password"
                                {...registerResume('password')}
                                error={Boolean(resumeErrors.password)}
                                onBlur={() => triggerResume('password')}
                            />
                            {resumeErrors?.password?.message && (
                                <FormHelperText sx={{ color: 'error.main' }}>
                                    {resumeErrors.password.message}
                                </FormHelperText>
                            )}
                        </FormControl>

                        <div className='actions-container' style={{ justifyContent: 'center', gap: '16px', marginTop: '28px' }}>
                            <RadiusButton variant='outlined' onClick={closeResumeModal} disabled={isResuming} sx={{ minWidth: 110 }}>
                                Cancel
                            </RadiusButton>
                            <LoadingButton sx={{ borderRadius: 40, minWidth: 110 }} variant='contained' type='submit' loading={isResuming}>
                                OK
                            </LoadingButton>
                        </div>
                    </Box>
                </Modal>

            </Container>
        </MainContainer>
    );
}
